import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useCallback, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

export type RankingItem = {
  id: string;
  label: string;
  description?: string;
};

type RankingProps = {
  question: string;
  description?: string;
  items: RankingItem[];
  onChange?: (orderedIds: string[]) => void;
  className?: string;
  testId?: string;
};

type SortableRowProps = {
  item: RankingItem;
  index: number;
  total: number;
  onMove: (index: number, delta: number) => void;
};

const GripIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
    <circle cx="4" cy="3" r="1.2" />
    <circle cx="10" cy="3" r="1.2" />
    <circle cx="4" cy="8" r="1.2" />
    <circle cx="10" cy="8" r="1.2" />
    <circle cx="4" cy="13" r="1.2" />
    <circle cx="10" cy="13" r="1.2" />
  </svg>
);

const SortableRow = ({ item, index, total, onMove }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  return (
    <li
      ref={setNodeRef}
      data-dragging={isDragging || undefined}
      className="flex items-center gap-3 rounded-xl border p-3 transition-[transform,opacity,box-shadow,border-color] duration-150 data-[dragging]:opacity-40"
      style={{
        borderColor: "hsl(var(--brand-bg-foreground) / 0.10)",
        background: "hsl(var(--brand-bg))",
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <span
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${item.label}`}
        className="flex shrink-0 cursor-grab touch-none items-center text-[hsl(var(--hero-fg-muted))] active:cursor-grabbing"
      >
        <GripIcon />
      </span>
      <span
        aria-hidden="true"
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums"
        style={{
          background: "hsl(var(--brand-primary) / 0.12)",
          color: "hsl(var(--brand-primary))",
        }}
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-bg-foreground))" }}>
          {item.label}
        </div>
        {item.description && (
          <div className="text-xs" style={{ color: "hsl(var(--hero-fg-muted))" }}>
            {item.description}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={`Move ${item.label} up`}
          onPress={() => onMove(index, -1)}
          isDisabled={index === 0}
        >
          <Icon icon="gravity-ui:arrow-up" width={14} height={14} />
        </Button>
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={`Move ${item.label} down`}
          onPress={() => onMove(index, 1)}
          isDisabled={index === total - 1}
        >
          <Icon icon="gravity-ui:arrow-down" width={14} height={14} />
        </Button>
      </div>
    </li>
  );
};

const RankingImpl = ({
  question,
  description,
  items,
  onChange,
  className,
  testId,
}: RankingProps) => {
  const [order, setOrder] = useState<string[]>(() => items.map((item) => item.id));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const from = prev.indexOf(String(active.id));
      const to = prev.indexOf(String(over.id));
      const next = arrayMove(prev, from, to);
      onChange?.(next);
      return next;
    });
  };

  const move = useCallback(
    (index: number, delta: number): void => {
      setOrder((prev) => {
        const target = index + delta;
        if (target < 0 || target >= prev.length) return prev;
        const next = arrayMove(prev, index, target);
        onChange?.(next);
        return next;
      });
    },
    [onChange],
  );

  const itemMap = new Map(items.map((item) => [item.id, item]));

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading
        title={question}
        description={description}
        hint="Drag the handle or use the up/down buttons."
        editablePath="question"
      />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ol role="list" aria-label="Ranked items" className="flex flex-col gap-2">
            {order.map((id, index) => {
              const item = itemMap.get(id);
              if (!item) return null;
              return (
                <SortableRow
                  key={id}
                  item={item}
                  index={index}
                  total={order.length}
                  onMove={move}
                />
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>
    </section>
  );
};

export const Ranking = memo(RankingImpl);

export default Ranking;
